import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, TrendingUp, AlertTriangle, BarChart3, PackageCheck, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function KPI({ label, value, sub, color = "var(--cobalt)", accent = "grad", icon: Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className="card-tpl p-4 relative overflow-hidden transition-all"
            style={{
                borderRadius: 16,
                border: "1px solid var(--line)",
                background: "#fff",
                boxShadow: "0 1px 0 rgba(0,0,0,0.02)",
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0 12px 32px -12px var(--mod-accent-glow)";
                e.currentTarget.style.borderColor = "var(--mod-accent)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 1px 0 rgba(0,0,0,0.02)";
                e.currentTarget.style.borderColor = "var(--line)";
            }}
        >
            <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: accent === "grad" ? "linear-gradient(90deg, var(--mod-accent), var(--mod-accent-deep))" : color }}></div>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: "var(--muted)", fontFamily: "JetBrains Mono, monospace" }}>{label}</div>
                {Icon && <Icon size={14} style={{ color: "var(--muted-2)" }} />}
            </div>
            <div className="text-[34px] font-extrabold tracking-[-0.035em] leading-none" style={{ color, fontFamily: "Geist, Outfit, sans-serif" }}>{value}</div>
            <div className="text-[11px] mt-2" style={{ color: "var(--muted)", fontFamily: "JetBrains Mono, monospace" }}>{sub}</div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [stages, setStages] = useState([]);
    const [jobs, setJobs] = useState([]);

    useEffect(() => {
        (async () => {
            const [s, w, j] = await Promise.all([
                api.get("/stats/dashboard"),
                api.get("/workflow"),
                api.get("/jobs"),
            ]);
            setStats(s.data);
            setStages(w.data);
            setJobs(j.data);
        })();
    }, []);

    const todayIt = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

    return (
        <div className="space-y-7" data-testid="dashboard-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1
                        className="tracking-tight"
                        style={{
                            color: "var(--navy)",
                            fontFamily: "Geist, Outfit, sans-serif",
                            fontSize: "clamp(1.8rem, 3vw, 2.4rem)",
                            fontWeight: 200,
                            letterSpacing: "-0.035em",
                            lineHeight: 1.05,
                        }}
                    >
                        Buongiorno, <span style={{ fontWeight: 600 }}>{user?.name?.split(" ")[0] || "Antonio"}</span>.
                    </h1>
                    <p className="text-[14px] mt-2" style={{ color: "var(--text-2)", fontFamily: "Outfit, sans-serif" }}>
                        Oggi è <b style={{ color: "var(--magenta)" }} className="mono">{todayIt}</b>
                        {stats?.total_jobs > 0 && <> · {stats.total_jobs} commesse attive</>}
                        {stats?.urgent_jobs > 0 && <> · <b style={{ color: "var(--magenta)" }}>{stats.urgent_jobs} urgenti</b></>}
                    </p>
                </div>
                <button
                    data-testid="dashboard-new-job"
                    onClick={() => nav("/commesse?new=1")}
                    className="btn-grad px-4 py-2.5 rounded-lg text-[13px] font-bold flex items-center gap-2"
                >
                    <Plus size={14} /> Nuova commessa
                </button>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI testid="kpi-total" label="Lavori totali" value={stats?.total_jobs ?? "—"} sub="Aggiornato live" color="var(--navy)" accent="grad" icon={TrendingUp} />
                <KPI testid="kpi-urgent" label="Urgenze" value={stats?.urgent_jobs ?? "—"} sub="Priorità alta" color="var(--magenta)" accent="magenta" icon={AlertTriangle} />
                <KPI testid="kpi-saturation" label="Saturazione" value={`${stats?.saturation_pct ?? 0}%`} sub="Reparto principale" color="var(--orange)" accent="orange" icon={BarChart3} />
                <KPI testid="kpi-ready" label="Pronte" value={stats?.ready_jobs ?? "—"} sub="Ritiro/spedizione" color="var(--green)" accent="green" icon={PackageCheck} />
            </div>

            {/* Workflow Kanban mini-preview */}
            <div>
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-5 rounded-sm" style={{ background: "var(--grad)" }}></div>
                    <h2 className="mono text-[12px] uppercase tracking-[0.12em] font-bold" style={{ color: "var(--navy)" }}>Workflow Commesse</h2>
                    <button onClick={() => nav("/commesse")} className="ml-auto text-[12px] font-semibold hover:underline" style={{ color: "var(--cobalt)" }}>
                        Vedi tutte →
                    </button>
                </div>

                {jobs.length === 0 ? (
                    <div className="card-tpl p-12 text-center">
                        <div className="text-[15px] font-bold mb-2" style={{ color: "var(--navy)" }}>Nessuna commessa ancora</div>
                        <p className="text-[13px] mb-5" style={{ color: "var(--text-2)" }}>Crea la prima commessa per iniziare a usare TotalPL.</p>
                        <button data-testid="empty-create-job" onClick={() => nav("/commesse?new=1")} className="btn-grad inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold">
                            <Plus size={14} /> Crea commessa
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {stages.slice(0, 5).map((s) => {
                            const colJobs = jobs.filter((j) => j.status_id === s.id);
                            return (
                                <div key={s.id} className="card-tpl p-3 min-h-[160px]">
                                    <div className="flex items-center justify-between pb-2 mb-2.5" style={{ borderBottom: "1px dashed var(--line-2)" }}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full" style={{ background: s.color }}></div>
                                            <div className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--navy)" }}>{s.name}</div>
                                        </div>
                                        <div className="mono text-[10px] font-bold px-1.5 py-0.5 rounded-md" style={{ color: "var(--muted)", background: "var(--bg)" }}>
                                            {colJobs.length}
                                        </div>
                                    </div>
                                    {colJobs.slice(0, 3).map((j) => (
                                        <div key={j.id}
                                            data-testid={`mini-job-${j.id}`}
                                            onClick={() => nav("/commesse")}
                                            className="mb-2 p-2.5 rounded-md hover:shadow-md cursor-pointer transition-all"
                                            style={{ background: "var(--bg)", border: "1px solid var(--line)", borderLeft: j.priority === "urgent" ? "3px solid var(--magenta)" : "1px solid var(--line)" }}>
                                            <div className="mono text-[9px] font-bold mb-1" style={{ color: "var(--magenta)" }}>#{j.job_number}</div>
                                            <div className="text-[12px] font-bold leading-tight mb-1" style={{ color: "var(--navy)" }}>{j.title}</div>
                                            {j.customer_name && <div className="text-[10px]" style={{ color: "var(--muted)" }}>{j.customer_name}</div>}
                                        </div>
                                    ))}
                                    {colJobs.length === 0 && <div className="text-[11px] italic text-center py-4" style={{ color: "var(--muted-2)" }}>—</div>}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Quick stats row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card-tpl p-5">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: "var(--cobalt-soft)" }}>
                            <Users size={16} style={{ color: "var(--cobalt)" }} />
                        </div>
                        <div>
                            <div className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--muted)" }}>Clienti</div>
                            <div className="text-[20px] font-extrabold" style={{ color: "var(--navy)" }}>{stats?.customers ?? "—"}</div>
                        </div>
                    </div>
                    <button onClick={() => nav("/clienti")} className="text-[12px] font-semibold hover:underline mt-1" style={{ color: "var(--cobalt)" }}>
                        Gestisci clienti →
                    </button>
                </div>

                <div className="card-tpl p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-md text-white" style={{ background: "var(--grad)" }}>NEXT</span>
                        <span className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--muted)" }}>Bot Telegram + AI</span>
                    </div>
                    <p className="text-[13px]" style={{ color: "var(--text-2)" }}>
                        Inoltra messaggi WhatsApp/email → AI crea commessa e cartelle automaticamente.
                    </p>
                </div>

                <div className="card-tpl p-5">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="mono text-[10px] uppercase tracking-[0.12em] font-bold px-2 py-0.5 rounded-md" style={{ background: "var(--cobalt-soft)", color: "var(--cobalt)" }}>TIP</span>
                        <span className="mono text-[10px] uppercase tracking-[0.1em] font-bold" style={{ color: "var(--muted)" }}>Inizio rapido</span>
                    </div>
                    <p className="text-[13px]" style={{ color: "var(--text-2)" }}>
                        Aggiungi il primo cliente, crea una commessa, sposta la card tra le colonne del Kanban.
                    </p>
                </div>
            </div>
        </div>
    );
}
